import React, { useState } from 'react';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';

export interface GreenhouseCredentialsFormProps {
  onSubmit: (clientId: string, clientSecret: string) => void;
  onTest: (
    clientId: string,
    clientSecret: string,
  ) => Promise<{ success: boolean; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export const GreenhouseCredentialsForm = ({
  onSubmit,
  onTest,
  isLoading,
  error,
}: GreenhouseCredentialsFormProps) => {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = () => {
    if (clientId.trim() && clientSecret.trim()) {
      onSubmit(clientId.trim(), clientSecret.trim());
    }
  };

  const handleTest = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    const result = await onTest(clientId.trim(), clientSecret.trim());

    setIsTesting(false);
    setTestResult({
      success: result.success,
      message: result.success
        ? 'Connection successful! Credentials are valid.'
        : result.error || 'Connection failed',
    });
  };

  const isValid = clientId.trim().length > 0 && clientSecret.trim().length > 0;

  return (
    <Spacing size="md" direction="vertical">
      <S2Typography
        variant="body-6"
        translatedContent="Enter your Greenhouse API credentials"
      />

      <TextInput
        type="password"
        label="Client ID"
        placeholder="Enter your Greenhouse Client ID"
        value={clientId}
        onChange={(e) => setClientId(e.currentTarget.value)}
        required
        disabled={isLoading || isTesting}
        data-qa="greenhouse-client-id-input"
      />

      <TextInput
        label="Client Secret"
        type="password"
        placeholder="Enter your Greenhouse Client Secret"
        value={clientSecret}
        onChange={(e) => setClientSecret(e.currentTarget.value)}
        required
        disabled={isLoading || isTesting}
        data-qa="greenhouse-client-secret-input"
      />

      {testResult && (
        <S2Typography
          variant="body-6"
          textColorVariant={testResult.success ? 'green-800' : 'red-500'}
          translatedContent={testResult.message}
        />
      )}

      {error && (
        <S2Typography
          variant="body-6"
          textColorVariant="red-500"
          translatedContent={error}
        />
      )}

      <Spacing size="sm" direction="horizontal">
        <Button
          variant="outline"
          size="medium"
          data-qa="greenhouse-test-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              void handleTest();
            },
          }}
          disabled={!isValid || isTesting || isLoading}
        >
          {isTesting ? 'Testing...' : 'Test Connection'}
        </Button>

        <Button
          variant="primary"
          size="medium"
          data-qa="greenhouse-submit-button"
          behavior={{
            type: 'onClick',
            onClick: () => {
              handleSubmit();
            },
          }}
          disabled={!isValid || isLoading || isTesting}
        >
          Connect Greenhouse
        </Button>
      </Spacing>
    </Spacing>
  );
};
